import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Box, CircularProgress, TextField, Typography } from "@mui/material";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import PersistedDataGrid from "../../PersistedDataGrid/PersistedDataGrid";
import { orientationExpenseTableColumns } from "../../../data/FormColumns/Recruiting/orientationExpenseTableColumns";
import { OrientationExpenseDto } from "../../../interfaces/Recruiting/orientation";

const fetchExpenses = async (
  token: string | null | undefined,
  orientationId?: string,
) => {
  if (!orientationId) return [];
  const { data } = await axios.get(
    `${apiVariable}/recruiting/orientations/${orientationId}/expenses`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  return data;
};

interface OrientationExpensesTableProps {
  orientationId?: string;
}

const OrientationExpensesTable = ({
  orientationId,
}: OrientationExpensesTableProps) => {
  const { token } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["orientationExpenses", orientationId],
    queryFn: () => fetchExpenses(token, orientationId),
    enabled: !!token && !!orientationId,
  });

  const filteredExpenses = useMemo(() => {
    if (!data) return [];
    return (data as OrientationExpenseDto[]).filter((expense) =>
      [
        expense.driverFirstName,
        expense.driverLastName,
        expense.expenseType,
        expense.paidBy,
        expense.notes,
      ].some((field) =>
        field?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    );
  }, [data, searchTerm]);

  if (!orientationId) {
    return (
      <Typography variant="body1" sx={{ mt: 2 }}>
        Select an orientation to view expenses.
      </Typography>
    );
  }

  return (
    <Box className="incidents-table-container" height={900}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "flex-end",
        }}
      >
        <TextField
          label="Search"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by driver, expense type, paid by, or notes"
          sx={{ maxWidth: "100%", width: 400 }}
        />
      </Box>
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center">
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error">Error: {(error as any).message}</Typography>
      ) : (
        <PersistedDataGrid
          tableName="orientationExpenses"
          rows={filteredExpenses}
          columns={orientationExpenseTableColumns(orientationId)}
          sx={{ height: 700 }}
        />
      )}
    </Box>
  );
};

export default OrientationExpensesTable;
